import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Title } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

import { getCatalogUpload } from '@src/apis/reware/catalog';
import type { CatalogUploadDetail } from '@src/apis/reware/catalog/getCatalogUpload';

import {
  POLL_INTERVAL_MS,
  TERMINAL_STATUSES,
  STATUS_CHIP_COLORS,
  STEP_LABELS,
  STEP_DESCRIPTIONS,
} from './constants';
import type { UploadStep, UploadStepStatus } from './types';

const STEP_ICONS: Record<string, React.ReactNode> = {
  catalog: <CloudUploadIcon color="primary" />,
  algolia: <SearchIcon color="action" />,
  shopify: <StorefrontIcon color="action" />,
};

const mapPhaseStatus = (status: string): UploadStepStatus => {
  switch (status) {
    case 'completed': return 'completed';
    case 'running':
    case 'importing': return 'running';
    case 'failed': return 'failed';
    case 'not_required': return 'not_required';
    default: return 'pending';
  }
};

const deriveSteps = (upload: CatalogUploadDetail): UploadStep[] => {
  const progress = upload.progress;

  if (progress) {
    // Use real progress data from the backend
    const catalogPhase = progress.catalogUpload;
    const algoliaPhase = progress.algoliaSync;
    const shopifyPhase = progress.shopifySync;

    return [
      {
        name: 'catalog',
        label: STEP_LABELS.catalog,
        description: STEP_DESCRIPTIONS.catalog,
        status: mapPhaseStatus(catalogPhase.status),
        current: catalogPhase.processedCount ?? 0,
        total: catalogPhase.totalCount ?? upload.totalRecords ?? 0,
      },
      {
        name: 'algolia',
        label: STEP_LABELS.algolia,
        description: STEP_DESCRIPTIONS.algolia,
        status: mapPhaseStatus(algoliaPhase.status),
        current: algoliaPhase.percentage > 0 ? Math.round((algoliaPhase.percentage / 100) * (upload.totalRecords ?? 0)) : 0,
        total: upload.totalRecords ?? 0,
      },
      {
        name: 'shopify',
        label: STEP_LABELS.shopify,
        description: STEP_DESCRIPTIONS.shopify,
        status: shopifyPhase.isRequired === true ?
          mapPhaseStatus(shopifyPhase.status) :
          (shopifyPhase.isRequired === false && catalogPhase.status === 'completed') ?
            'not_required' :
            'unknown',
        current: shopifyPhase.isRequired === false ? 0 :
          (shopifyPhase.percentage > 0 ? Math.round((shopifyPhase.percentage / 100) * (upload.totalRecords ?? 0)) : 0),
        total: shopifyPhase.isRequired === false ? 0 : (upload.totalRecords ?? 0),
      },
    ];
  }

  // Fallback: derive from uploadStatus when progress field is unavailable
  const status = upload.uploadStatus ?? 'pending';
  const total = upload.totalRecords ?? 0;
  const data = upload.data ?? {};
  const processed = (data.added ?? 0) + (data.updated ?? 0) + (data.skipped ?? 0);
  const isTerminal = TERMINAL_STATUSES.has(status);
  const isCompleted = status === 'completed' || status === 'success';
  const isFailed = status === 'failed';

  let catalogStatus: UploadStepStatus = 'pending';
  if (isCompleted) catalogStatus = 'completed';
  else if (isFailed) catalogStatus = 'failed';
  else if (status === 'processing') catalogStatus = 'running';

  let algoliaStatus: UploadStepStatus = 'pending';
  if (isCompleted) algoliaStatus = 'completed';
  else if (isFailed && catalogStatus === 'completed') algoliaStatus = 'failed';

  return [
    {
      name: 'catalog',
      label: STEP_LABELS.catalog,
      description: STEP_DESCRIPTIONS.catalog,
      status: catalogStatus,
      current: isTerminal ? total : processed,
      total,
    },
    {
      name: 'algolia',
      label: STEP_LABELS.algolia,
      description: STEP_DESCRIPTIONS.algolia,
      status: algoliaStatus,
      current: algoliaStatus === 'completed' ? total : 0,
      total,
    },
    {
      name: 'shopify',
      label: STEP_LABELS.shopify,
      description: STEP_DESCRIPTIONS.shopify,
      status: 'unknown',
      current: 0,
      total: 0,
    },
  ];
};

const getOverallProgress = (steps: UploadStep[]): number => {
  const activeSteps = steps.filter(s => s.status !== 'not_required' && s.status !== 'unknown');
  if (activeSteps.length === 0) return 0;

  const perStep = 100 / activeSteps.length;
  let progress = 0;

  for (const step of activeSteps) {
    if (step.status === 'completed') {
      progress += perStep;
    } else if (step.status === 'running' && step.total > 0) {
      progress += perStep * (step.current / step.total);
    }
  }

  return Math.round(progress);
};

interface StepCardProps {
  step: UploadStep;
  icon: React.ReactNode;
  isActive: boolean;
}

const StepCard: React.FC<StepCardProps> = ({ step, icon, isActive }) => {
  const percent = step.total > 0 ? Math.round((step.current / step.total) * 100) : 0;

  const chipColor =
    step.status === 'completed' ?
      'success' :
      step.status === 'running' ?
        'primary' :
        step.status === 'failed' ?
          'error' :
          'default';

  const statusLabel =
    step.status === 'not_required' ?
      'Not Required' :
      step.status === 'unknown' ?
        'TBD' :
        step.status.charAt(0).toUpperCase() + step.status.slice(1);

  const isDisabled = step.status === 'not_required' || step.status === 'unknown';

  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        borderColor: isActive ? 'primary.main' : undefined,
        borderWidth: isActive ? 2 : 1,
        opacity: isDisabled ? 0.6 : 1,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Typography variant="subtitle2" fontWeight="bold" noWrap>
              {step.label}
            </Typography>
          </Box>
          {step.status === 'running' || step.status === 'completed' || step.status === 'failed' ? (
            <Chip label={statusLabel} color={chipColor} size="small" />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {statusLabel}
            </Typography>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {step.description}
        </Typography>

        {step.status === 'not_required' ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Not required
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &mdash;
            </Typography>
          </Box>
        ) : step.status === 'unknown' ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              To be determined
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &mdash;
            </Typography>
          </Box>
        ) : step.status === 'pending' ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Waiting to start
            </Typography>
            <Typography variant="body2" color="text.secondary">
              0%
            </Typography>
          </Box>
        ) : (
          <>
            <LinearProgress
              variant="determinate"
              value={percent}
              color={step.status === 'completed' ? 'success' : 'primary'}
              sx={{ mb: 1, height: 6, borderRadius: 3 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                {step.current}
                {' / '}
                {step.total}
                {' rows'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {percent}
                {'%'}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const UploadProgressPage: React.FC = () => {
  const { uploadId } = useParams<{ uploadId: string }>();
  const navigate = useNavigate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [upload, setUpload] = useState<CatalogUploadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUpload = useCallback(async () => {
    const id = Number(uploadId);
    if (isNaN(id)) {
      setError('Invalid upload ID');
      setLoading(false);
      return;
    }

    try {
      const data = await getCatalogUpload(id);
      setUpload(data);
      setLoading(false);

      // Schedule next poll if not in terminal state
      const overallStatus = data.progress?.overallStatus ?? data.uploadStatus ?? '';
      const normalizedStatus = overallStatus.toLowerCase();
      if (!TERMINAL_STATUSES.has(normalizedStatus)) {
        timeoutRef.current = setTimeout(() => {
          void fetchUpload();
        }, POLL_INTERVAL_MS);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load upload');
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => {
    void fetchUpload();
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [fetchUpload]);

  const handleRefresh = () => {
    clearTimeout(timeoutRef.current);
    void fetchUpload();
  };

  const handleBack = () => {
    void navigate('/catalog-uploader');
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !upload) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title="Upload Progress" />
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ?? 'Upload not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Catalog Uploader
        </Button>
      </Box>
    );
  }

  const steps = deriveSteps(upload);
  const overallProgress = getOverallProgress(steps);
  const overallStatus = upload.progress?.overallStatus ?? upload.uploadStatus ?? 'unknown';
  const isCompleted = overallStatus === 'completed' || overallStatus === 'success';
  const isFailed = overallStatus === 'failed' || upload.uploadStatus === 'failed';
  const statusColor = STATUS_CHIP_COLORS[overallStatus] ?? STATUS_CHIP_COLORS[upload.uploadStatus ?? ''] ?? 'default';
  const statusLabel = overallStatus === 'processing' ? 'importing' : overallStatus;

  const catalogPhaseCompleted = upload.progress?.catalogUpload.status === 'completed';
  const shopifyIsRequired = upload.progress?.shopifySync.isRequired === true ? true :
    (upload.progress?.shopifySync.isRequired === false && catalogPhaseCompleted) ? false :
    undefined;

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Upload Progress" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h4" fontWeight="bold">
          Upload Progress
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Catalog Uploader
        </Button>
      </Box>

      {/* Upload info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {upload.title ?? `Upload #${upload.id}`}
        </Typography>
        <Chip label={statusLabel} color={statusColor} size="small" />
      </Box>

      {/* Main content card */}
      <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 3 }}>
        {/* Overall progress */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Overall progress</Typography>
            <Typography variant="subtitle2">
              {overallProgress}
              {'%'}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            color={isCompleted ? 'success' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Step cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {steps.map(step => (
            <StepCard
              key={step.name}
              step={step}
              icon={STEP_ICONS[step.name]}
              isActive={step.status === 'running'}
            />
          ))}
        </Box>

        {/* Info note */}
        {shopifyIsRequired === false && (
          <Alert severity="info" icon={false} sx={{ bgcolor: 'info.50' }}>
            <Typography variant="body2">
              {(upload.data?.updated ?? 0) === 0 && (upload.data?.deleted ?? 0) === 0
                ? 'Since this upload resulted in only new SKU variants being created, a Shopify sync is not required.'
                : 'Shopify sync is not required because this partner does not have Shopify configured as a commerce platform.'}
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Completion banner */}
      {isCompleted && (
        <Alert
          severity={upload.data?.errorCount ? 'warning' : 'success'}
          icon={<CheckCircleOutlineIcon />}
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={handleRefresh}>
              Refresh
            </Button>
          }
          sx={{ mt: 2 }}
        >
          <Typography variant="subtitle2">
            {upload.data?.errorCount
              ? 'Upload completed with errors'
              : 'All jobs completed successfully!'}
          </Typography>
          <Typography variant="body2">
            {'Successfully processed '}
            {(upload.totalRecords ?? 0) - (upload.data?.errorCount ?? 0)}
            {' of '}
            {upload.totalRecords ?? 0}
            {' rows, synced to Algolia'}
            {shopifyIsRequired === true ? ' and Shopify' : ''}
            {'. '}
            {upload.data?.errorCount
              ? `${upload.data.errorCount} row${upload.data.errorCount === 1 ? '' : 's'} had errors.`
              : ''}
          </Typography>
        </Alert>
      )}

      {/* Failure banner */}
      {isFailed && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Upload failed</Typography>
          <Typography variant="body2">
            {upload.data?.errorCount ?
              `${upload.data.errorCount} errors encountered during processing.` :
              'An error occurred during processing.'}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default UploadProgressPage;
